"use client";

import React, { useEffect, useRef, type ComponentProps, type RefObject } from "react";
import { Slot } from "./Slot";

export const Anchor = ({
	asChild,
	children,
	containerSelector,
	containerRef,
	...props
}: ComponentProps<"div"> & {
	asChild?: boolean;
	containerSelector?: string;
	containerRef?: RefObject<HTMLElement | null>;
}) => {
	const Component = asChild ? Slot : "div";
	const ref = useRef<HTMLDivElement>(null!);

	useEffect(() => {
		if (!ref.current) return;
		const current = ref.current;

		const unsubscribe = trackElementPosition(current, function updatePosition(positions) {
			const target = containerRef
				? containerRef.current || current
				: containerSelector
					? document.querySelector<HTMLElement>(containerSelector) || current
					: current;

			target.style.setProperty("--anchor-top", `${positions.top}px`);
			target.style.setProperty("--anchor-left", `${positions.left}px`);
			target.style.setProperty("--anchor-width", `${positions.width}px`);
			target.style.setProperty("--anchor-height", `${positions.height}px`);
			target.style.setProperty("--anchor-bottom", `${positions.bottom}px`);
			target.style.setProperty("--anchor-right", `${positions.right}px`);
			target.style.setProperty("--anchor-x", `${positions.x}px`);
			target.style.setProperty("--anchor-y", `${positions.y}px`);
		});

		return () => {
			unsubscribe?.();
		};
	}, [containerSelector, containerRef]);

	return (
		<Component ref={ref} {...props}>
			{children}
		</Component>
	);
};

/**
 * Tracks the position of an element and calls a callback with position data
 * whenever the position changes (during scroll, resize, and transform)
 * @param {HTMLElement} target - The element to track
 * @param {Function} callback - Function to call with position data
 * @param {Object} options - Configuration options
 * @param {boolean} options.includeAbsolute - Whether to include document-relative positions
 * @param {number} options.throttleMs - Throttle updates by this many milliseconds (0 for no throttle)
 */
function trackElementPosition(
	target: HTMLElement,
	callback: (
		position: Record<"top" | "left" | "width" | "height" | "bottom" | "right" | "y" | "x", number>,
	) => void,
	{ throttleMs = 0 } = {},
) {
	if (!target) {
		console.error("Target element not found");
		return;
	}

	if (typeof callback !== "function") {
		console.error("Callback must be a function");
		return;
	}

	let lastCallTime = 0;
	let pendingUpdate = false;

	// Function to get position data and call the callback
	function updatePosition() {
		if (throttleMs > 0) {
			const now = Date.now();
			if (now - lastCallTime < throttleMs) {
				if (!pendingUpdate) {
					pendingUpdate = true;
					setTimeout(
						() => {
							updatePosition();
							pendingUpdate = false;
						},
						throttleMs - (now - lastCallTime),
					);
				}
				return;
			}
			lastCallTime = now;
		}

		const rect = target.getBoundingClientRect();

		// Get viewport-relative positions
		const positions = {
			top: rect.top,
			left: rect.left,
			width: rect.width,
			height: rect.height,
			bottom: rect.bottom,
			right: rect.right,
			x: rect.x,
			y: rect.y,
		};

		//   // If requested, add document-relative positions (including scroll)
		//   if (includeAbsolute) {
		// 	const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
		// 	const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

		// 	positions.absoluteTop = positions.top + scrollTop;
		// 	positions.absoluteLeft = positions.left + scrollLeft;
		// 	positions.absoluteBottom = positions.bottom + scrollTop;
		// 	positions.absoluteRight = positions.right + scrollLeft;
		//   }

		// Call the callback with the position data
		callback(positions);
	}

	// Initial update
	updatePosition();

	// Set up IntersectionObserver to detect visibility changes
	const intersectionObserver = new IntersectionObserver(
		(entries) => {
			updatePosition();
		},
		{
			threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1],
		},
	);

	intersectionObserver.observe(target);

	// Set up ResizeObserver to detect size changes
	const resizeObserver = new ResizeObserver((entries) => {
		updatePosition();
	});

	resizeObserver.observe(target);

	// Monitor scroll events
	window.addEventListener("scroll", updatePosition, { passive: true });

	// Monitor resize events
	window.addEventListener("resize", updatePosition, { passive: true });

	// Monitor transforms and other layout changes using animation frames
	let ticking = false;

	function requestUpdate() {
		if (!ticking) {
			requestAnimationFrame(() => {
				updatePosition();
				ticking = false;
			});
			ticking = true;
		}
	}

	// Additional events that might affect position
	window.addEventListener("transitionend", updatePosition);
	window.addEventListener("animationend", updatePosition);

	// For any DOM mutations that might affect layout
	const mutationObserver = new MutationObserver(requestUpdate);

	mutationObserver.observe(document.body, {
		attributes: true,
		childList: true,
		subtree: true,
		attributeFilter: ["style", "class"],
	});

	// Return a cleanup function
	return function cleanup() {
		intersectionObserver.disconnect();
		resizeObserver.disconnect();
		mutationObserver.disconnect();
		window.removeEventListener("scroll", updatePosition);
		window.removeEventListener("resize", updatePosition);
		window.removeEventListener("transitionend", updatePosition);
		window.removeEventListener("animationend", updatePosition);
	};
}

// Example usage with a callback that sets CSS variables:
/*
  const element = document.querySelector('#my-element');
  const cleanup = trackElementPosition(element, (positions, element) => {
	// Set CSS variables
	element.style.setProperty("--anchor-top", `${positions.top}px`);
	element.style.setProperty("--anchor-left", `${positions.left}px`);
	element.style.setProperty("--anchor-width", `${positions.width}px`);
	element.style.setProperty("--anchor-height", `${positions.height}px`);
	element.style.setProperty("--anchor-bottom", `${positions.bottom}px`);
	element.style.setProperty("--anchor-right", `${positions.right}px`);
	element.style.setProperty("--anchor-x", `${positions.x}px`);
	element.style.setProperty("--anchor-y", `${positions.y}px`);
	
	// Use absolute positions if available
	if (positions.absoluteTop !== undefined) {
	  element.style.setProperty("--anchor-absolute-top", `${positions.absoluteTop}px`);
	  element.style.setProperty("--anchor-absolute-left", `${positions.absoluteLeft}px`);
	  element.style.setProperty("--anchor-absolute-bottom", `${positions.absoluteBottom}px`);
	  element.style.setProperty("--anchor-absolute-right", `${positions.absoluteRight}px`);
	}
	
	// You can also do other things with the position data
	console.log('Element is at:', positions);
  }, { throttleMs: 16 }); // Optional throttling at ~60fps
  
  // When you want to stop tracking:
  // cleanup();
  */
